import { useEffect } from "react";
import { grey } from "@mui/material/colors";
import { SurveyResponseBreakdown } from "catalog/types/interfaces/conceptResponse";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { COLOR_PALETTE } from "~/customer/chartJS/colors";

export const useRegisterChartElements = () => {
  useEffect(() => {
    ChartJS.register(
      CategoryScale,
      LinearScale,
      Filler,
      BarElement,
      PointElement,
      LineElement,
      Title,
      Tooltip,
      Legend
    );
  }, []);
};

export const DEFAULT_OPTS: any = {
  animation: false,
  font: {
    size: 14,
  },
  scale: {
    grid: {
      drawTicks: false,
    },
  },
  datasets: {
    bar: {
      maxBarThickness: 50,
      backgroundColor: COLOR_PALETTE[0],
    },
  },
  plugins: {
    tooltip: {
      yAlign: "bottom",
    },
  },
};

/**
 * Helper function to handle wrapping ChartJS labels by converting to array
 * @param label - Original label text
 * @param keyword - Word to split label on, e.g. "or"
 */
export const splitAxisLabel = (label: string, keyword: string): string[] => {
  const splitText = label.split(keyword);
  return splitText.map((text, index) => {
    if (index < splitText.length - 1) {
      return text + keyword;
    }
    return text;
  });
};

/**
 * Transforms a participant response breakdown list into ChartJS data and labels
 * where x-axis data is survey year and y-axis is number of responses (split by demographic)
 * @param data - Participant breakdown count data
 */
export const mapResponseBreakdown = (data: SurveyResponseBreakdown[]) => {
  const dataMap = new Map<number, { [key: string]: number }>();
  const conceptNames = new Set<string>();

  data
    .sort((a, b) => b.response_count - a.response_count)
    .forEach(({ survey_year, concept_name, response_count }) => {
      conceptNames.add(concept_name);
      if (!dataMap.has(survey_year)) {
        dataMap.set(survey_year, {});
      }
      const yearData = dataMap.get(survey_year)!;
      yearData[concept_name] = (yearData[concept_name] || 0) + response_count;
    });

  const labels = Array.from(dataMap.keys()).sort((a, b) => a - b);
  const datasets = Array.from(conceptNames).map((concept_name, index) => ({
    label: concept_name,
    data: labels.map((year) => dataMap.get(year)![concept_name] || 0),
    backgroundColor: COLOR_PALETTE[index] ?? grey[300],
  }));

  return { labels, datasets };
};

export const isHighCardinalityStackedBar = (...args: ChartData<"bar">[]): boolean => {
  const threshold = 5;
  return args.some(
    (data) => Number(data.labels?.length) > threshold || data.datasets.length > threshold
  );
};
